import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

interface MapsKeyResponse {
  googleMapsApiKey: string;
}

declare global {
  interface Window {
    google?: typeof google;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GooglePlacesService {
  private readonly http = inject(HttpClient);
  private readonly configUrl = `${environment.apiBaseUrl}/config/google-maps-key`;
  private keyPromise: Promise<string> | null = null;
  private scriptPromise: Promise<typeof google> | null = null;

  private async fetchApiKey(): Promise<string> {
    if (!this.keyPromise) {
      this.keyPromise = firstValueFrom(this.http.get<MapsKeyResponse>(this.configUrl))
        .then((res) => {
          if (!res.googleMapsApiKey) {
            throw new Error('Google Maps API key is not configured on the server.');
          }
          return res.googleMapsApiKey;
        })
        .catch((error) => {
          this.keyPromise = null;
          throw error;
        });
    }

    return this.keyPromise;
  }

  private async ensurePlacesLibrary(): Promise<typeof google> {
    if (typeof window === 'undefined') {
      throw new Error('Google Places API is only available in the browser.');
    }

    if (window.google?.maps?.places) {
      return window.google;
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    const apiKey = await this.fetchApiKey();
    this.scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=fr`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-google-maps', 'true');
      script.onload = () => {
        if (window.google?.maps?.places) {
          resolve(window.google);
        } else {
          this.scriptPromise = null;
          reject(new Error('Google Maps script loaded but Places library is unavailable.'));
        }
      };
      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Failed to load the Google Maps JavaScript API.'));
      };

      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  async createAutocomplete(
    input: HTMLInputElement,
    options?: google.maps.places.AutocompleteOptions
  ): Promise<google.maps.places.Autocomplete> {
    const googleLib = await this.ensurePlacesLibrary();
    return new googleLib.maps.places.Autocomplete(input, options);
  }
}
